'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Server, Globe, Shield, Clock, Search, Edit, Trash2, ExternalLink, ChevronUp, ChevronDown } from "lucide-react"
import { getCountryFlag } from "@/lib/utils"
import { toast } from "@/components/ui/use-toast"
import { useAuth } from "@/hooks/use-auth"
import { ROLES } from "@/lib/constants"
import { ServerForm } from "@/components/dashboard/server-form"
import { ServerStatsCards } from "@/components/server-stats-cards"

type SortField = 'displayName' | 'region' | 'status' | 'hostname' | 'sponsor' | 'ipv6'
type SortDirection = 'asc' | 'desc'

interface ServerData {
  id: string
  hostname: string
  displayName: string
  countryCode: string
  city?: string
  region: 'Americas' | 'Asia-Pacific' | 'Europe' | 'IMEA' | 'Global'
  ipv4?: string
  ipv6?: string
  ports?: number[]
  sslPorts?: number[]
  contactUrl?: string
  status?: string
  flags?: string[]
  maxUsers?: number
  currentUsers?: number
  uptimeSeconds?: number
  sponsorId?: string
  isActive?: boolean
  lastUpdated?: string
  createdAt?: string
  createdBy?: string
  sponsor?: {
    id: string
    name: string
    url: string
    logoUrl?: string
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "online":
      return "bg-emerald-500"
    case "offline":
      return "bg-destructive"
    default:
      return "bg-amber-500"
  }
}

const getLocationDisplay = (city?: string, region?: string) => {
  if (city && region) {
    return `${city}, ${region}`
  }
  return region || 'Unknown location'
}

const getConnectUrl = (hostname: string, port: number = 6667) => {
  return `irc://${hostname}:${port}`
}

const getDisplayPorts = (ports: number[] = [], sslPorts: number[] = [], ircSslEnabled: boolean) => {
  if (ircSslEnabled) {
    return ports
  }
  // Filter out SSL ports (6697, 994, 7000, 7001, 7070, 9999) when SSL is disabled
  const commonSslPorts = [6697, 994, 7000, 7001, 7070, 9999]
  return ports.filter(port => !commonSslPorts.includes(port))
}

interface ServersClientProps {
  ircSslEnabled: boolean
}

export function ServersClient({ ircSslEnabled }: ServersClientProps) {
  const [servers, setServers] = useState<ServerData[]>([])
  const [filteredServers, setFilteredServers] = useState<ServerData[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [regionFilter, setRegionFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [ipv6Filter, setIpv6Filter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [isMobile, setIsMobile] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedServer, setSelectedServer] = useState<ServerData | null>(null)
  const [sortField, setSortField] = useState<SortField>('displayName')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const { user, hasRole, loading: authLoading } = useAuth()

  const canEditServers = !authLoading && user && (hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.SERVER_ADMIN))

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
      setViewMode(window.innerWidth < 768 ? 'cards' : 'table')
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Fetch public servers immediately, don't wait for auth
    fetchServers()
  }, [])

  // Refetch servers when authentication state changes (to get admin data if user logs in)
  useEffect(() => {
    if (!authLoading) {
      fetchServers()
    }
  }, [canEditServers])

  useEffect(() => {
    filterServers()
  }, [servers, searchTerm, regionFilter, statusFilter, ipv6Filter, sortField, sortDirection])

  const fetchServers = async () => {
    try {
      // Always use public endpoint initially, or admin endpoint if user has permissions
      const endpoint = canEditServers ? '/api/servers' : '/api/servers/public'
      const response = await fetch(endpoint)
      if (response.ok) {
        const data = await response.json()
        setServers(data)
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch servers',
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Error fetching servers:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch servers',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const filterServers = () => {
    let filtered = servers.filter(server => {
      const matchesSearch = server.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           server.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (server.city && server.city.toLowerCase().includes(searchTerm.toLowerCase()))
      
      const matchesRegion = regionFilter === 'all' || server.region === regionFilter
      const matchesStatus = statusFilter === 'all' || server.status === statusFilter
      const matchesIPv6 = ipv6Filter === 'all' || 
                         (ipv6Filter === 'yes' && hasIPv6Support(server)) ||
                         (ipv6Filter === 'no' && !hasIPv6Support(server))
      
      return matchesSearch && matchesRegion && matchesStatus && matchesIPv6
    })
    
    // Apply sorting
    filtered = sortServers(filtered)
    
    setFilteredServers(filtered)
  }

  const handleDelete = async (serverId: string) => {
    if (!confirm('Are you sure you want to delete this server?')) return

    try {
      const response = await fetch('/api/servers', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: serverId })
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Server deleted successfully'
        })
        fetchServers()
      } else {
        const error = await response.json()
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete server',
          variant: 'destructive'
        })
      }
    } catch (error) {
      console.error('Error deleting server:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete server',
        variant: 'destructive'
      })
    }
  }

  const canEditServer = (server: ServerData) => {
    if (!user) return false
    
    if (hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.SERVER_ADMIN, 'global')) {
      return true
    }
    
    return hasRole(ROLES.SERVER_ADMIN, 'server', server.id)
  }

  const canDeleteServer = (server: ServerData) => {
    if (!user) return false
    
    return hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.SERVER_ADMIN, 'global')
  }

  const handleEditServer = (server: ServerData) => {
    setSelectedServer(server)
    setShowEditModal(true)
  }

  const isRoundRobinServer = (server: ServerData) => {
    return server.flags?.includes('RR') || false
  }

  const getServerTypeLabels = (flags: string[] = []) => {
    const labels = []
    
    if (flags.includes('RR')) {
      labels.push({ text: 'DNS RR', variant: 'secondary' as const })
    }
    
    if (flags.includes('Anycast')) {
      labels.push({ text: 'Anycast', variant: 'outline' as const })
    }
    
    if (flags.includes('IX')) {
      labels.push({ text: 'IX', variant: 'outline' as const })
    }
    
    return labels
  }

  const hasIPv6Support = (server: ServerData) => {
    return server.ipv6 && server.ipv6.trim() !== ''
  }

  const hasIPv4Support = (server: ServerData) => {
    return server.ipv4 && server.ipv4.trim() !== ''
  }

  const getIPProtocolsLabel = (server: ServerData) => {
    const hasV4 = hasIPv4Support(server)
    const hasV6 = hasIPv6Support(server)
    const isRR = isRoundRobinServer(server)
    
    // DNS RR servers without IP addresses should show empty
    if (isRR && !hasV4 && !hasV6) return ''
    
    if (hasV4 && hasV6) return 'IPv4 + IPv6'
    if (hasV6) return 'IPv6 only'
    if (hasV4) return 'IPv4 only'
    return 'No IP configured'
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortServers = (servers: ServerData[]) => {
    return [...servers].sort((a, b) => {
      // Primary sort: Global region first
      const aIsGlobal = a.region === 'Global'
      const bIsGlobal = b.region === 'Global'
      
      if (aIsGlobal !== bIsGlobal) {
        return aIsGlobal ? -1 : 1
      }

      // Secondary sort: DNS round robin servers (RR flag)
      const aIsRoundRobin = isRoundRobinServer(a)
      const bIsRoundRobin = isRoundRobinServer(b)
      
      if (aIsRoundRobin !== bIsRoundRobin) {
        return aIsRoundRobin ? -1 : 1
      }

      // Tertiary sort: Alphabetical by name (default) or user-selected field
      let comparison = 0
      switch (sortField) {
        case 'displayName':
          comparison = a.displayName.localeCompare(b.displayName)
          break
        case 'hostname':
          comparison = a.hostname.localeCompare(b.hostname)
          break
        case 'region':
          comparison = a.region.localeCompare(b.region)
          break
        case 'status':
          comparison = (a.status || 'unknown').localeCompare(b.status || 'unknown')
          break
        case 'sponsor':
          const aSponsor = a.sponsor?.name || ''
          const bSponsor = b.sponsor?.name || ''
          comparison = aSponsor.localeCompare(bSponsor)
          break
        case 'ipv6':
          const aHasV6 = hasIPv6Support(a)
          const bHasV6 = hasIPv6Support(b)
          if (aHasV6 !== bHasV6) {
            comparison = aHasV6 ? -1 : 1
          } else {
            comparison = a.displayName.localeCompare(b.displayName)
          }
          break
        default:
          comparison = a.displayName.localeCompare(b.displayName)
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }

  const ServerCard = ({ server }: { server: ServerData }) => (
    <Card key={server.id} className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${getStatusColor(server.status || 'unknown')}`} />
            <div>
              <div className="flex items-center space-x-2">
                <CardTitle className="text-lg">{server.displayName}</CardTitle>
                {getServerTypeLabels(server.flags).map((label, index) => (
                  <Badge key={index} variant={label.variant} className="text-xs">
                    {label.text}
                  </Badge>
                ))}
              </div>
              <CardDescription className="font-mono text-sm">{server.hostname}</CardDescription>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-2xl">{getCountryFlag(server.countryCode)}</div>
            {canEditServers && (
              <div className="flex space-x-1">
                {canEditServer(server) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditServer(server)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {canDeleteServer(server) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(server.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          <span>{getLocationDisplay(server.city, server.region)}</span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">IPv4:</span>
            <code className="bg-muted px-2 py-1 rounded text-xs">{server.ipv4 || 'N/A'}</code>
          </div>
          {server.ipv6 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">IPv6:</span>
              <code className="bg-muted px-2 py-1 rounded text-xs">{server.ipv6}</code>
            </div>
          )}
          {getIPProtocolsLabel(server) && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Protocol:</span>
              <Badge 
                variant={hasIPv6Support(server) ? "default" : "secondary"} 
                className="text-xs"
              >
                {getIPProtocolsLabel(server)}
              </Badge>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">Ports:</span>
          <div className="flex flex-wrap gap-1">
            {(() => {
              const displayPorts = getDisplayPorts(server.ports, server.sslPorts, ircSslEnabled)
              return displayPorts.length > 0 ? displayPorts.map((port) => (
                <Badge key={port} variant="secondary" className="text-xs">
                  {port}
                </Badge>
              )) : <span className="text-xs text-muted-foreground">No ports specified</span>
            })()}
          </div>
        </div>

        {server.flags && server.flags.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Features:</span>
            <div className="flex flex-wrap gap-1">
              {server.flags.map((flag) => (
                <Badge key={flag} variant="outline" className="text-xs">
                  {flag === "ssl" && <Shield className="w-3 h-3 mr-1" />}
                  {flag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {server.sponsor && (
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>Sponsored by:</span>
            <a 
              href={server.sponsor.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              {server.sponsor.name}
            </a>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Clock className="h-3 w-3" />
            <span>Updated {server.lastUpdated ? new Date(server.lastUpdated).toLocaleDateString() : 'Unknown'}</span>
          </div>
          <Badge variant={server.status === "online" ? "default" : "destructive"} className="text-xs">
            {server.status || 'unknown'}
          </Badge>
        </div>

        <Button 
          className="w-full" 
          variant={server.status === "online" ? "default" : "secondary"}
          onClick={() => {
            const displayPorts = getDisplayPorts(server.ports, server.sslPorts, ircSslEnabled)
            const connectPort = displayPorts[0] || server.ports?.[0] || 6667
            window.open(getConnectUrl(server.hostname, connectPort), '_blank')
          }}
        >
          <Server className="mr-2 h-4 w-4" />
          Connect to {server.displayName}
        </Button>
      </CardContent>
    </Card>
  )

  const SortableTableHead = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead>
      <Button
        variant="ghost"
        className="h-auto p-0 font-semibold hover:bg-transparent"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          {sortField === field && (
            sortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </Button>
    </TableHead>
  )

  const ServerTable = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead field="displayName">Server</SortableTableHead>
            <SortableTableHead field="region">Location</SortableTableHead>
            <SortableTableHead field="status">Status</SortableTableHead>
            <TableHead>Ports</TableHead>
            <SortableTableHead field="sponsor">Sponsor</SortableTableHead>
            <SortableTableHead field="ipv6">Support</SortableTableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredServers.map((server) => (
            <TableRow key={server.id}>
              <TableCell>
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(server.status || 'unknown')}`} />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{server.displayName}</span>
                      {getServerTypeLabels(server.flags).map((label, index) => (
                        <Badge key={index} variant={label.variant} className="text-xs">
                          {label.text}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-sm text-muted-foreground font-mono">{server.hostname}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getCountryFlag(server.countryCode)}</span>
                  <span>{getLocationDisplay(server.city, server.region)}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={server.status === "online" ? "default" : "destructive"} className="text-xs">
                  {server.status || 'unknown'}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {(() => {
                    const displayPorts = getDisplayPorts(server.ports, server.sslPorts, ircSslEnabled)
                    const visiblePorts = displayPorts.slice(0, 3)
                    return (
                      <>
                        {visiblePorts.map((port) => (
                          <Badge key={port} variant="secondary" className="text-xs">
                            {port}
                          </Badge>
                        ))}
                        {displayPorts.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{displayPorts.length - 3}
                          </Badge>
                        )}
                      </>
                    )
                  })()}
                </div>
              </TableCell>
              <TableCell>
                {server.sponsor ? (
                  <a 
                    href={server.sponsor.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm"
                  >
                    {server.sponsor.name}
                  </a>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </TableCell>
              <TableCell>
                {getIPProtocolsLabel(server) && (
                  <Badge 
                    variant={hasIPv6Support(server) ? "default" : "secondary"} 
                    className="text-xs"
                  >
                    {getIPProtocolsLabel(server)}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const displayPorts = getDisplayPorts(server.ports, server.sslPorts, ircSslEnabled)
                      const connectPort = displayPorts[0] || server.ports?.[0] || 6667
                      window.open(getConnectUrl(server.hostname, connectPort), '_blank')
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Connect
                  </Button>
                  {canEditServers && (
                    <>
                      {canEditServer(server) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditServer(server)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDeleteServer(server) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(server.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )

  if (loading || authLoading) {
    return (
      <>
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Undernet Servers</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Connect to any of our global servers to join the Undernet IRC network. Choose the server closest to your
            location for the best experience.
          </p>
        </div>

        {/* Loading Skeleton */}
        <div className="space-y-8">
          {/* Statistics Cards Skeleton */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse"></div>
            ))}
          </div>
          
          {/* Filters Skeleton */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 h-10 bg-muted rounded animate-pulse"></div>
            <div className="w-full md:w-48 h-10 bg-muted rounded animate-pulse"></div>
            <div className="w-full md:w-48 h-10 bg-muted rounded animate-pulse"></div>
            <div className="w-full md:w-48 h-10 bg-muted rounded animate-pulse"></div>
          </div>
          
          {/* Server Cards Skeleton */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-muted rounded-lg animate-pulse"></div>
            ))}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Undernet Servers</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Connect to any of our global servers to join the Undernet IRC network. Choose the server closest to your
          location for the best experience.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="mb-8">
        <ServerStatsCards servers={servers} />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search servers..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            <SelectItem value="Americas">Americas</SelectItem>
            <SelectItem value="Asia-Pacific">Asia-Pacific</SelectItem>
            <SelectItem value="Europe">Europe</SelectItem>
            <SelectItem value="IMEA">IMEA</SelectItem>
            <SelectItem value="Global">Global</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ipv6Filter} onValueChange={setIpv6Filter}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by IPv6" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Protocols</SelectItem>
            <SelectItem value="yes">IPv6 Enabled</SelectItem>
            <SelectItem value="no">IPv4 Only</SelectItem>
          </SelectContent>
        </Select>
        {!isMobile && (
          <Select value={viewMode} onValueChange={(value) => setViewMode(value as 'cards' | 'table')}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="table">Table</SelectItem>
              <SelectItem value="cards">Cards</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Server List */}
      {filteredServers.length > 0 ? (
        <>
          {viewMode === 'table' && !isMobile ? (
            <ServerTable />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredServers.map((server) => (
                <ServerCard key={server.id} server={server} />
              ))}
            </div>
          )}
        </>
      ) : (
        <Card className="p-12 text-center">
          <CardContent>
            <Server className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <CardTitle className="text-xl mb-2">No Servers Found</CardTitle>
            <CardDescription>
              {searchTerm || regionFilter !== 'all' || statusFilter !== 'all'
                ? 'No servers match your current filters. Try adjusting your search criteria.'
                : 'There are currently no active servers available. Please check back later or contact support.'}
            </CardDescription>
          </CardContent>
        </Card>
      )}

      {/* Connection Instructions */}
      <Card className="mt-12">
        <CardHeader>
          <CardTitle>How to Connect</CardTitle>
          <CardDescription>Instructions for connecting to Undernet servers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Using an IRC Client</h4>
              <p className="text-sm text-muted-foreground mb-2">Configure your IRC client with these settings:</p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Server: Choose from the list above</li>
                <li>• Port: 6667 (standard) or 6668, 6669</li>
                {ircSslEnabled && <li>• SSL: Use port 6697 for SSL/TLS connections</li>}
                <li>• Network: Undernet</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Web-based Chat</h4>
              <p className="text-sm text-muted-foreground mb-4">For quick access, use our web-based IRC client:</p>
              <Button asChild>
                <a href="https://chat.undernet.org" target="_blank" rel="noopener noreferrer">
                  Open webchat
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Server Modal */}
      {showEditModal && selectedServer && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Server</DialogTitle>
              <DialogDescription>
                Update server configuration and settings
              </DialogDescription>
            </DialogHeader>
            <ServerForm
              server={{
                ...selectedServer,
                sponsor: undefined // Remove sponsor from server object for form
              } as any}
              onCancel={() => setShowEditModal(false)}
              onSuccess={() => {
                setShowEditModal(false)
                setSelectedServer(null)
                fetchServers()
              }}
              ircSslEnabled={ircSslEnabled}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}